import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  BellAlertIcon,
  BoltIcon,
  CheckCircleIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  ShieldExclamationIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageLayout from '../../components/PageLayout';
import { platformAPI } from '../../services/api';
import CustomerDetailDrawer from '../../components/customer/CustomerDetailDrawer';
import {
  AdminFilterCard,
  AdminModalShell,
  AdminPageHeader,
  AdminStatCard,
  AdminStatGrid,
  AdminTableCard,
} from '../../components/AdminSurface';

const formatDateInputValue = (value: Date) => value.toISOString().slice(0, 10);

const getDefaultHistoryStartDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return formatDateInputValue(date);
};

const getDefaultHistoryEndDate = () => formatDateInputValue(new Date());

const SAVED_BILLING_VIEWS_KEY = 'platform-billing-saved-views-v1';
const MAX_SAVED_BILLING_VIEWS = 8;

type PlatformBillingSavedViewState = {
  companyId: string;
  limit: number;
  historyStartDate: string;
  historyEndDate: string;
  alertTargetFilter: string;
  alertSeverityFilter: string;
  alertDeliveryStatusFilter: string;
  alertWorkflowStatusFilter: string;
  webhookStatusFilter: string;
  webhookEventTypeFilter: string;
  invoiceJobStatusFilter: string;
  invoiceIdFilter: string;
};

type PlatformBillingSavedView = {
  id: string;
  name: string;
  state: PlatformBillingSavedViewState;
  createdAt: string;
  updatedAt: string;
};

const loadSavedBillingViews = (): PlatformBillingSavedView[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const rawValue = window.localStorage.getItem(SAVED_BILLING_VIEWS_KEY);
    if (!rawValue) {
      return [];
    }

    const parsed = JSON.parse(rawValue);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

function PlatformBilling() {
  const queryClient = useQueryClient();
  const [companyId, setCompanyId] = useState('');
  const [limit, setLimit] = useState(25);
  const [historyStartDate, setHistoryStartDate] = useState(getDefaultHistoryStartDate);
  const [historyEndDate, setHistoryEndDate] = useState(getDefaultHistoryEndDate);
  const [alertTargetFilter, setAlertTargetFilter] = useState('');
  const [alertSeverityFilter, setAlertSeverityFilter] = useState('');
  const [alertDeliveryStatusFilter, setAlertDeliveryStatusFilter] = useState('');
  const [alertWorkflowStatusFilter, setAlertWorkflowStatusFilter] = useState('');
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);
  const [webhookStatusFilter, setWebhookStatusFilter] = useState('');
  const [webhookEventTypeFilter, setWebhookEventTypeFilter] = useState('');
  const [invoiceJobStatusFilter, setInvoiceJobStatusFilter] = useState('');
  const [invoiceIdFilter, setInvoiceIdFilter] = useState('');
  const [alertPage, setAlertPage] = useState(1);
  const [webhookEventsPage, setWebhookEventsPage] = useState(1);
  const [invoiceSyncJobsPage, setInvoiceSyncJobsPage] = useState(1);
  const [selectedWebhookEventIds, setSelectedWebhookEventIds] = useState<string[]>([]);
  const [selectedInvoiceSyncJobIds, setSelectedInvoiceSyncJobIds] = useState<string[]>([]);
  const [savedViewName, setSavedViewName] = useState('');
  const [savedViews, setSavedViews] = useState<PlatformBillingSavedView[]>(loadSavedBillingViews);
  const [activeSavedViewId, setActiveSavedViewId] = useState<string | null>(null);
  const [exportingDataset, setExportingDataset] = useState<string | null>(null);
  const [selectedFeeInvoice, setSelectedFeeInvoice] = useState<any | null>(null);
  const [selectedFeeCustomer, setSelectedFeeCustomer] = useState<any | null>(null);
  const [historyPanelsEnabled, setHistoryPanelsEnabled] = useState(false);

  const historyParams = {
    startDate: historyStartDate || undefined,
    endDate: historyEndDate || undefined,
  };

  const queryParams = {
    companyId: companyId || undefined,
    limit,
    staleMinutes: 15,
    maxAttempts: 5,
  };

  const getCurrentSavedViewState = (): PlatformBillingSavedViewState => ({
    companyId,
    limit,
    historyStartDate,
    historyEndDate,
    alertTargetFilter,
    alertSeverityFilter,
    alertDeliveryStatusFilter,
    alertWorkflowStatusFilter,
    webhookStatusFilter,
    webhookEventTypeFilter,
    invoiceJobStatusFilter,
    invoiceIdFilter,
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(SAVED_BILLING_VIEWS_KEY, JSON.stringify(savedViews));
  }, [savedViews]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setHistoryPanelsEnabled(true);
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      setHistoryPanelsEnabled(true);
    });

    return () => window.cancelAnimationFrame(frame);
  }, []);

  const { data: companies = [] } = useQuery(
    'platform-billing-companies',
    () => platformAPI.getCompanies(),
    {
      select: (response) => {
        const data = response.data;
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.companies)) return data.companies;
        return [];
      },
    },
  );

  const { data: health, isLoading: healthLoading } = useQuery(
    'platform-billing-health',
    () => platformAPI.getBillingHealth(),
    { select: (response) => response.data },
  );

  const { data: stripeAccountHealth, isLoading: stripeAccountHealthLoading } = useQuery(
    ['platform-stripe-account-health', companyId, limit],
    () => platformAPI.getStripeAccountHealth({ companyId: companyId || undefined, limit }),
    { keepPreviousData: true, select: (response) => response.data },
  );

  const { data: summary, isLoading: summaryLoading } = useQuery(
    ['platform-billing-summary', companyId],
    () => platformAPI.getBillingSummary({ companyId: companyId || undefined, staleMinutes: 15, maxAttempts: 5 }),
    {
      keepPreviousData: true,
      select: (response) => response.data,
    },
  );

  const { data: reconciliation, isLoading: reconciliationLoading } = useQuery(
    ['platform-billing-reconciliation', queryParams],
    () => platformAPI.getBillingReconciliationSummary(queryParams),
    {
      keepPreviousData: true,
      select: (response) => response.data,
    },
  );

  const { data: alertsResponse, isLoading: alertsLoading } = useQuery(
    ['platform-billing-alerts', alertTargetFilter, alertSeverityFilter, alertDeliveryStatusFilter, alertWorkflowStatusFilter, limit, alertPage, historyStartDate, historyEndDate],
    () => platformAPI.getBillingAlerts({
      limit,
      page: alertPage,
      target: alertTargetFilter || undefined,
      severity: alertSeverityFilter || undefined,
      deliveryStatus: alertDeliveryStatusFilter || undefined,
      workflowStatus: alertWorkflowStatusFilter || undefined,
      ...historyParams,
    }),
    {
      enabled: historyPanelsEnabled,
      keepPreviousData: true,
      select: (response) => response.data,
    },
  );

  const { data: selectedAlert, isLoading: selectedAlertLoading } = useQuery(
    ['platform-billing-alert-detail', selectedAlertId],
    () => platformAPI.getBillingAlert(selectedAlertId as string),
    {
      enabled: Boolean(selectedAlertId),
      select: (response) => response.data,
    },
  );

  const { data: webhookEventsResponse, isLoading: webhookEventsLoading } = useQuery(
    ['platform-billing-webhook-events', companyId, limit, webhookEventsPage, webhookStatusFilter, webhookEventTypeFilter, historyStartDate, historyEndDate],
    () => platformAPI.getWebhookEvents({
      companyId: companyId || undefined,
      limit,
      page: webhookEventsPage,
      status: webhookStatusFilter || undefined,
      eventType: webhookEventTypeFilter || undefined,
      ...historyParams,
    }),
    {
      enabled: historyPanelsEnabled,
      keepPreviousData: true,
      select: (response) => response.data,
    },
  );

  const { data: invoiceSyncJobsResponse, isLoading: invoiceSyncJobsLoading } = useQuery(
    ['platform-billing-invoice-sync-jobs', companyId, limit, invoiceSyncJobsPage, invoiceJobStatusFilter, invoiceIdFilter, historyStartDate, historyEndDate],
    () => platformAPI.getInvoiceSyncJobs({
      companyId: companyId || undefined,
      limit,
      page: invoiceSyncJobsPage,
      status: invoiceJobStatusFilter || undefined,
      invoiceId: invoiceIdFilter || undefined,
      ...historyParams,
    }),
    {
      enabled: historyPanelsEnabled,
      keepPreviousData: true,
      select: (response) => response.data,
    },
  );

  const { data: paidInvoicesActivityResponse, isLoading: paidInvoicesActivityLoading } = useQuery(
    ['platform-billing-paid-invoices-activity', companyId],
    () => platformAPI.getInvoices({
      status: 'paid',
      companyId: companyId || undefined,
      page: 1,
      limit: 100,
    }),
    {
      enabled: historyPanelsEnabled,
      keepPreviousData: true,
      select: (response) => response.data,
    },
  );

  const shouldLookupSelectedCustomer = Boolean(
    selectedFeeCustomer?.companyId &&
      (selectedFeeCustomer?.customerId || (selectedFeeCustomer?.customerEmail && selectedFeeCustomer.customerEmail.includes('@'))),
  );

  const { data: selectedFeeCustomerDetailsResponse, isLoading: selectedFeeCustomerDetailsLoading } = useQuery(
    ['platform-selected-customer-profile', selectedFeeCustomer?.companyId, selectedFeeCustomer?.customerId, selectedFeeCustomer?.customerEmail],
    () => platformAPI.getCompanyCustomerProfile(selectedFeeCustomer.companyId, {
      customerId: selectedFeeCustomer?.customerId || undefined,
      email: selectedFeeCustomer?.customerId ? undefined : selectedFeeCustomer?.customerEmail,
    }),
    {
      enabled: shouldLookupSelectedCustomer,
      retry: false,
      select: (response) => response.data,
    },
  );

  const alerts = alertsResponse?.alerts || [];
  const webhookEvents = webhookEventsResponse?.events || [];
  const invoiceSyncJobs = invoiceSyncJobsResponse?.jobs || [];

  const invalidateBillingQueries = () => {
    queryClient.invalidateQueries('platform-billing-health');
    queryClient.invalidateQueries('platform-billing-summary');
    queryClient.invalidateQueries('platform-billing-reconciliation');
    queryClient.invalidateQueries(['platform-billing-alerts']);
    queryClient.invalidateQueries(['platform-billing-alert-detail']);
    queryClient.invalidateQueries(['platform-billing-webhook-events']);
    queryClient.invalidateQueries(['platform-billing-invoice-sync-jobs']);
    queryClient.invalidateQueries(['platform-stripe-account-health']);
  };

  const stripeAccountHealthSweepMutation = useMutation(
    () => platformAPI.runStripeAccountHealthSweep(),
    {
      onSuccess: (response) => {
        toast.success(`Stripe account health sweep ${response.data?.status || 'completed'}`);
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to run Stripe account health sweep');
      },
    },
  );

  const webhookRecoveryMutation = useMutation(
    () => platformAPI.recoverWebhookEvents({ limit, staleMinutes: 15, maxAttempts: 5 }),
    {
      onSuccess: () => {
        toast.success('Webhook recovery sweep queued');
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to recover webhook events');
      },
    },
  );

  const invoiceRecoveryMutation = useMutation(
    () => platformAPI.recoverInvoiceSyncJobs({ limit, staleMinutes: 15, maxAttempts: 5 }),
    {
      onSuccess: () => {
        toast.success('Invoice sync recovery sweep queued');
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to recover invoice sync jobs');
      },
    },
  );

  const resyncMutation = useMutation(
    () => platformAPI.resyncOpenInvoices({ companyId: companyId || undefined, limit }),
    {
      onSuccess: (response) => {
        toast.success(`Queued ${response.data?.queued || 0} invoice sync jobs`);
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to resync open invoices');
      },
    },
  );

  const repairMutation = useMutation(
    () => platformAPI.repairStripeStateDrift({ companyId: companyId || undefined, limit }),
    {
      onSuccess: (response) => {
        toast.success(`Repaired ${response.data?.repaired || 0} actionable Stripe drift records`);
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to repair Stripe state drift');
      },
    },
  );

  const customerRepairMutation = useMutation(
    () => platformAPI.repairCustomerMappings({ companyId: companyId || undefined, limit }),
    {
      onSuccess: (response) => {
        toast.success(`Repaired ${response.data?.repaired || 0} customer mapping records`);
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to repair customer mappings');
      },
    },
  );

  const feeRepairMutation = useMutation(
    () => platformAPI.repairFeeConsistency({ companyId: companyId || undefined, limit }),
    {
      onSuccess: (response) => {
        toast.success(`Refreshed ${response.data?.repaired || 0} fee parity records`);
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to refresh fee consistency');
      },
    },
  );

  const disputeRepairMutation = useMutation(
    () => platformAPI.repairDisputeConsistency({ companyId: companyId || undefined, limit }),
    {
      onSuccess: (response) => {
        toast.success(`Repaired ${response.data?.repaired || 0} dispute consistency records`);
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to repair dispute consistency');
      },
    },
  );

  const reviewDisputeCandidateMutation = useMutation(
    ({ id, workflowStatus, note }: { id: string; workflowStatus: 'reviewed' | 'resolved'; note?: string }) =>
      platformAPI.reviewDisputeCandidate(id, { workflowStatus, note }, { companyId: companyId || undefined }),
    {
      onSuccess: (response, variables) => {
        toast.success(variables.workflowStatus === 'resolved'
          ? 'Dispute candidate resolved'
          : 'Dispute candidate marked reviewed');
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update dispute review state');
      },
    },
  );

  const acknowledgeAlertMutation = useMutation(
    ({ id, note }: { id: string; note?: string }) => platformAPI.acknowledgeBillingAlert(id, note),
    {
      onSuccess: () => {
        toast.success('Billing alert acknowledged');
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to acknowledge billing alert');
      },
    },
  );

  const handoffAlertMutation = useMutation(
    ({ id, provider, reference, note }: { id: string; provider: string; reference?: string; note?: string }) =>
      platformAPI.handoffBillingAlert(id, { provider, reference, note }),
    {
      onSuccess: () => {
        toast.success('Billing alert handed off');
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to hand off billing alert');
      },
    },
  );

  const resolveAlertMutation = useMutation(
    ({ id, note }: { id: string; note: string }) => platformAPI.resolveBillingAlert(id, note),
    {
      onSuccess: () => {
        toast.success('Billing alert resolved');
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to resolve billing alert');
      },
    },
  );

  const retryAlertMutation = useMutation(
    (id: string) => platformAPI.retryBillingAlert(id),
    {
      onSuccess: () => {
        toast.success('Billing alert retry completed');
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to retry billing alert');
      },
    },
  );

  const retryFailedAlertsMutation = useMutation(
    () => platformAPI.retryFailedBillingAlerts({ limit: 10 }),
    {
      onSuccess: (response) => {
        toast.success(`Retried ${response.data?.retried || 0} failed billing alerts`);
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to run billing alert retry pass');
      },
    },
  );

  const replayWebhookMutation = useMutation(
    (id: string) => platformAPI.replayWebhookEvent(id),
    {
      onSuccess: () => {
        toast.success('Webhook replay completed');
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to replay webhook event');
      },
    },
  );

  const bulkReplayWebhookMutation = useMutation(
    (ids: string[]) => platformAPI.replayWebhookEventsBulk(ids),
    {
      onSuccess: (response) => {
        toast.success(`Replayed ${response.data?.processed || 0} webhook events`);
        setSelectedWebhookEventIds([]);
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to bulk replay webhook events');
      },
    },
  );

  const retryInvoiceSyncJobMutation = useMutation(
    (id: string) => platformAPI.retryInvoiceSyncJob(id),
    {
      onSuccess: () => {
        toast.success('Invoice sync retry completed');
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to retry invoice sync job');
      },
    },
  );

  const bulkRetryInvoiceSyncJobMutation = useMutation(
    (ids: string[]) => platformAPI.retryInvoiceSyncJobsBulk(ids),
    {
      onSuccess: (response) => {
        toast.success(`Retried ${response.data?.completed || 0} invoice sync jobs`);
        setSelectedInvoiceSyncJobIds([]);
        invalidateBillingQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to bulk retry invoice sync jobs');
      },
    },
  );

  const resetHistorySelections = () => {
    setSelectedAlertId(null);
    setSelectedWebhookEventIds([]);
    setSelectedInvoiceSyncJobIds([]);
  };

  const resetPagination = () => {
    setAlertPage(1);
    setWebhookEventsPage(1);
    setInvoiceSyncJobsPage(1);
  };

  const resetInvestigationState = () => {
    setActiveSavedViewId(null);
    resetHistorySelections();
    resetPagination();
  };

  const deliveryTone = (status?: string) => {
    if (status === 'failed') return 'bg-error-container text-error-on-container';
    if (status === 'partial' || status === 'suppressed') return 'bg-warning-container text-warning-on-container';
    if (status === 'unconfigured') return 'bg-surface-container-low text-on-surface-variant';
    return 'bg-success-container text-success-on-container';
  };

  const severityTone = (severity?: string) => {
    if (severity === 'error') return 'bg-error-container text-error-on-container';
    return 'bg-warning-container text-warning-on-container';
  };

  const formatDateTime = (value?: string | Date | null) => {
    if (!value) return 'Never';
    return new Date(value).toLocaleString();
  };

  const formatLabel = (value?: string | null) => {
    if (!value) return 'Unknown';
    return value.replaceAll('_', ' ');
  };

  const workflowTone = (status?: string) => {
    if (status === 'resolved') return 'bg-success-container text-success-on-container';
    if (status === 'handed_off') return 'bg-warning-container text-warning-on-container';
    if (status === 'acknowledged') return 'bg-surface-container-high text-on-surface';
    return 'bg-surface-container-low text-on-surface-variant';
  };

  const historyWindowLabel = historyStartDate && historyEndDate
    ? `${historyStartDate} to ${historyEndDate}`
    : historyStartDate || historyEndDate || 'All available history';

  const getPageRangeLabel = (page: number, pageSize: number, currentCount: number, total: number) => {
    if (currentCount === 0 || total === 0) {
      return 'Showing 0 of 0';
    }

    const start = ((page - 1) * pageSize) + 1;
    const end = start + currentCount - 1;
    return `Showing ${start}-${end} of ${total}`;
  };

  const downloadBlobResponse = (response: any, fallbackFilename: string) => {
    const blob = response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const disposition = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
    const matchedFilename = typeof disposition === 'string'
      ? disposition.match(/filename="?([^";]+)"?/)?.[1]
      : undefined;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = matchedFilename || fallbackFilename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async (dataset: 'alerts' | 'webhook-events' | 'invoice-sync-jobs') => {
    setExportingDataset(dataset);

    try {
      const exportLimit = Math.max(limit, 500);
      if (dataset === 'alerts') {
        const response = await platformAPI.exportBillingAlerts({
          limit: exportLimit,
          target: alertTargetFilter || undefined,
          severity: alertSeverityFilter || undefined,
          deliveryStatus: alertDeliveryStatusFilter || undefined,
          workflowStatus: alertWorkflowStatusFilter || undefined,
          ...historyParams,
        });
        downloadBlobResponse(response, 'billing-alerts.csv');
        toast.success('Exported filtered billing alerts');
        return;
      }

      if (dataset === 'webhook-events') {
        const response = await platformAPI.exportWebhookEvents({
          companyId: companyId || undefined,
          limit: exportLimit,
          status: webhookStatusFilter || undefined,
          eventType: webhookEventTypeFilter || undefined,
          ...historyParams,
        });
        downloadBlobResponse(response, 'billing-webhook-events.csv');
        toast.success('Exported filtered webhook events');
        return;
      }

      const response = await platformAPI.exportInvoiceSyncJobs({
        companyId: companyId || undefined,
        limit: exportLimit,
        status: invoiceJobStatusFilter || undefined,
        invoiceId: invoiceIdFilter || undefined,
        ...historyParams,
      });
      downloadBlobResponse(response, 'billing-invoice-sync-jobs.csv');
      toast.success('Exported filtered invoice sync jobs');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to export billing history');
    } finally {
      setExportingDataset(null);
    }
  };

  const handleAcknowledgeAlert = (alert: any) => {
    const note = window.prompt('Optional acknowledgement note', alert.acknowledgement_note || '');
    if (note === null) {
      return;
    }

    acknowledgeAlertMutation.mutate({ id: alert.id, note: note || undefined });
  };

  const handleHandoffAlert = (alert: any) => {
    const provider = window.prompt('External handoff provider', alert.handoff_provider || 'pagerduty');
    if (provider === null) {
      return;
    }

    const normalizedProvider = provider.trim();
    if (!normalizedProvider) {
      toast.error('A handoff provider is required');
      return;
    }

    const reference = window.prompt('External incident or ticket reference', alert.handoff_reference || '');
    if (reference === null) {
      return;
    }

    const note = window.prompt('Handoff note', alert.handoff_note || alert.acknowledgement_note || '');
    if (note === null) {
      return;
    }

    handoffAlertMutation.mutate({
      id: alert.id,
      provider: normalizedProvider,
      reference: reference || undefined,
      note: note || undefined,
    });
  };

  const handleResolveAlert = (alert: any) => {
    const note = window.prompt('Resolution note', alert.resolution_note || '');
    if (note === null) {
      return;
    }

    const normalizedNote = note.trim();
    if (!normalizedNote) {
      toast.error('A resolution note is required');
      return;
    }

    resolveAlertMutation.mutate({ id: alert.id, note: normalizedNote });
  };

  const handleReviewDisputeCandidate = (candidate: any, workflowStatus: 'reviewed' | 'resolved') => {
    const promptLabel = workflowStatus === 'resolved'
      ? 'Resolution note'
      : 'Optional review note';
    const note = window.prompt(promptLabel, candidate.manual_review_note || '');
    if (note === null) {
      return;
    }

    const normalizedNote = note.trim();
    if (workflowStatus === 'resolved' && !normalizedNote) {
      toast.error('A resolution note is required');
      return;
    }

    reviewDisputeCandidateMutation.mutate({
      id: candidate.dispute_id,
      workflowStatus,
      note: normalizedNote || undefined,
    });
  };

  const saveCurrentView = () => {
    const trimmedName = savedViewName.trim();
    if (!trimmedName) {
      toast.error('Enter a saved view name first');
      return;
    }

    const existingView = savedViews.find((view) => view.name.toLowerCase() === trimmedName.toLowerCase());
    const timestamp = new Date().toISOString();
    const nextId = existingView?.id || `billing-view-${Date.now()}`;
    const nextView: PlatformBillingSavedView = {
      id: nextId,
      name: trimmedName,
      state: getCurrentSavedViewState(),
      createdAt: existingView?.createdAt || timestamp,
      updatedAt: timestamp,
    };

    setSavedViews((currentViews) => [
      nextView,
      ...currentViews.filter((view) => view.id !== nextId),
    ].slice(0, MAX_SAVED_BILLING_VIEWS));
    setActiveSavedViewId(nextId);
    toast.success(existingView ? 'Saved view updated' : 'Saved view created');
  };

  const applySavedView = (view: PlatformBillingSavedView) => {
    setCompanyId(view.state.companyId);
    setLimit(view.state.limit);
    setHistoryStartDate(view.state.historyStartDate);
    setHistoryEndDate(view.state.historyEndDate);
    setAlertTargetFilter(view.state.alertTargetFilter);
    setAlertSeverityFilter(view.state.alertSeverityFilter);
    setAlertDeliveryStatusFilter(view.state.alertDeliveryStatusFilter);
    setAlertWorkflowStatusFilter(view.state.alertWorkflowStatusFilter);
    setWebhookStatusFilter(view.state.webhookStatusFilter);
    setWebhookEventTypeFilter(view.state.webhookEventTypeFilter);
    setInvoiceJobStatusFilter(view.state.invoiceJobStatusFilter);
    setInvoiceIdFilter(view.state.invoiceIdFilter);
    setActiveSavedViewId(view.id);
    resetHistorySelections();
    resetPagination();
    setSavedViewName(view.name);
    toast.success(`Applied saved view: ${view.name}`);
  };

  const deleteSavedView = (viewId: string) => {
    setSavedViews((currentViews) => currentViews.filter((view) => view.id !== viewId));
    if (activeSavedViewId === viewId) {
      setActiveSavedViewId(null);
    }
  };

  const resetFiltersToDefaults = () => {
    setCompanyId('');
    setLimit(25);
    setHistoryStartDate(getDefaultHistoryStartDate());
    setHistoryEndDate(getDefaultHistoryEndDate());
    setAlertTargetFilter('');
    setAlertSeverityFilter('');
    setAlertDeliveryStatusFilter('');
    setAlertWorkflowStatusFilter('');
    setWebhookStatusFilter('');
    setWebhookEventTypeFilter('');
    setInvoiceJobStatusFilter('');
    setInvoiceIdFilter('');
    setActiveSavedViewId(null);
    resetHistorySelections();
    resetPagination();
  };

  const toggleSelection = (
    id: string,
    selectedIds: string[],
    setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    setSelectedIds(selectedIds.includes(id)
      ? selectedIds.filter((selectedId) => selectedId !== id)
      : [...selectedIds, id]);
  };

  const toggleVisibleSelection = (
    ids: string[],
    selectedIds: string[],
    setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    const allVisibleSelected = ids.length > 0 && ids.every((id) => selectedIds.includes(id));
    if (allVisibleSelected) {
      setSelectedIds(selectedIds.filter((id) => !ids.includes(id)));
      return;
    }

    setSelectedIds(Array.from(new Set([...selectedIds, ...ids])));
  };

  const visibleWebhookEventIds = webhookEvents.map((event: any) => event.id);
  const visibleInvoiceSyncJobIds = invoiceSyncJobs.map((job: any) => job.id);

  const renderPaginationControls = (args: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    currentCount: number;
    onPageChange: (nextPage: number) => void;
  }) => {
    return (
      <div className="mt-4 flex flex-col gap-3 border-t border-outline px-6 py-4 text-xs text-on-surface-variant sm:flex-row sm:items-center sm:justify-between">
        <div>{getPageRangeLabel(args.page, args.pageSize, args.currentCount, args.total)} · Page {args.page} of {args.totalPages}</div>
        <div className="flex items-center gap-2">
          <button
            className="btn-secondary text-xs"
            onClick={() => args.onPageChange(args.page - 1)}
            disabled={args.page <= 1}
          >
            Previous
          </button>
          <button
            className="btn-secondary text-xs"
            onClick={() => args.onPageChange(args.page + 1)}
            disabled={args.page >= args.totalPages}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  const alertSummary = {
    total: alertsResponse?.total || 0,
    visible: alerts.length,
    failed: alerts.filter((alert: any) => alert.delivery_status === 'failed').length,
    manualAction: alerts.filter((alert: any) => alert.metadata?.alert_delivery?.manual_action_required).length,
    unacknowledged: alerts.filter((alert: any) => !alert.acknowledged_at).length,
    handedOff: alerts.filter((alert: any) => alert.workflow_status === 'handed_off').length,
    resolved: alerts.filter((alert: any) => alert.workflow_status === 'resolved').length,
  };

  const webhookSummary = {
    total: webhookEventsResponse?.total || 0,
    visible: webhookEvents.length,
    failed: webhookEvents.filter((event: any) => event.status === 'failed').length,
    deadLetter: webhookEvents.filter((event: any) => event.status === 'dead_letter').length,
    processing: webhookEvents.filter((event: any) => event.status === 'processing').length,
  };

  const invoiceJobSummary = {
    total: invoiceSyncJobsResponse?.total || 0,
    visible: invoiceSyncJobs.length,
    failed: invoiceSyncJobs.filter((job: any) => job.status === 'failed').length,
    deadLetter: invoiceSyncJobs.filter((job: any) => job.status === 'dead_letter').length,
    processing: invoiceSyncJobs.filter((job: any) => job.status === 'processing').length,
  };

  const collectedFeeActivity = useMemo(() => {
    const paidInvoices = Array.isArray(paidInvoicesActivityResponse?.invoices)
      ? paidInvoicesActivityResponse.invoices
      : [];

    return paidInvoices
      .map((invoice: any) => {
        const feeCents = Number(invoice.platform_fee || 0);
        if (!Number.isFinite(feeCents) || feeCents <= 0) {
          return null;
        }

        return {
          id: invoice.id,
          invoiceNumber: invoice.invoice_number || 'N/A',
          companyId: invoice.company_id || null,
          companySlug: invoice.company_slug || null,
          companyName: invoice.company_name || invoice.company_slug || invoice.company_id || 'Unknown company',
          customerId: invoice.customer_id || null,
          customerName: invoice.customer_name || null,
          customerEmail: invoice.customer_email || 'Unknown customer',
          paidAt: invoice.paid_at ? new Date(invoice.paid_at) : null,
          feeUsd: feeCents / 100,
          totalUsd: Number(invoice.total || 0) / 100,
          stripeInvoiceId: invoice.stripe_invoice_id || null,
          stripeReference: invoice.stripe_reference || invoice.stripe_invoice_id || invoice.payment_intent_id || null,
          stripeReferenceType: invoice.stripe_reference_type || (invoice.stripe_invoice_id ? 'invoice' : invoice.payment_intent_id ? 'payment_intent' : null),
          groupedPaymentRequestId: invoice.grouped_payment_request_id || null,
          groupedPaymentRequestInvoiceId: invoice.grouped_payment_request_stripe_invoice_id || null,
          groupedPaymentRequestPaymentIntentId: invoice.grouped_payment_request_payment_intent_id || null,
        };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => (b.paidAt?.getTime() || 0) - (a.paidAt?.getTime() || 0))
      .slice(0, 12);
  }, [paidInvoicesActivityResponse]);

  const historyPanelsLoading = !historyPanelsEnabled || alertsLoading || webhookEventsLoading || invoiceSyncJobsLoading;

  const formatUsd = (value: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number.isFinite(value) ? value : 0);

  const openInvoiceDetails = (invoiceId: string) => {
    const selectedEntry = collectedFeeActivity.find((entry: any) => entry.id === invoiceId) || null;
    setSelectedFeeInvoice(selectedEntry);
  };

  const openCustomerDrawer = (entry: any) => {
    if (!entry) return;

    setSelectedFeeCustomer({
      customerId: entry.customerId || null,
      customerName: entry.customerName || null,
      customerEmail: entry.customerEmail || 'Unknown customer',
      companyId: entry.companyId || null,
      companyName: entry.companyName || 'Unknown company',
    });
  };

  const selectedCustomerFeeEvents = useMemo(() => {
    if (!selectedFeeCustomer) {
      return [];
    }

    return collectedFeeActivity.filter((entry: any) => {
      const sameCompany = selectedFeeCustomer.companyId
        ? entry.companyId === selectedFeeCustomer.companyId
        : true;

      if (selectedFeeCustomer.customerId && entry.customerId) {
        return sameCompany && entry.customerId === selectedFeeCustomer.customerId;
      }

      return sameCompany && entry.customerEmail === selectedFeeCustomer.customerEmail;
    });
  }, [collectedFeeActivity, selectedFeeCustomer]);

  return (
    <PageLayout>
      <AdminPageHeader
        eyebrow="Platform billing"
        title="Platform Billing Operations"
        description="Monitor billing health, recovery backlogs, alert delivery, and reconciliation drift across connected accounts."
        actions={(
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <select
            className="input-field min-w-[220px]"
            value={companyId}
            onChange={(event) => {
              setCompanyId(event.target.value);
              resetInvestigationState();
            }}
          >
            <option value="">All companies</option>
            {companies.map((company: any) => (
              <option key={company.id} value={company.id}>{company.name}</option>
            ))}
          </select>
          <select
            className="input-field"
            value={limit}
            onChange={(event) => {
              setLimit(Number.parseInt(event.target.value, 10));
              resetInvestigationState();
            }}
          >
            <option value={25}>25 records</option>
            <option value={50}>50 records</option>
            <option value={100}>100 records</option>
            <option value={250}>250 records</option>
          </select>
          <input
            type="date"
            className="input-field"
            value={historyStartDate}
            onChange={(event) => {
              setHistoryStartDate(event.target.value);
              resetInvestigationState();
            }}
          />
          <input
            type="date"
            className="input-field"
            value={historyEndDate}
            onChange={(event) => {
              setHistoryEndDate(event.target.value);
              resetInvestigationState();
            }}
          />
          </div>
        )}
      />

      <>
          <AdminFilterCard
            className="mb-8"
            title="History Window"
            description={`Detailed alert, webhook, and invoice-sync lists below are filtered to ${historyWindowLabel}. Health and recovery cards remain live operational totals.`}
            actions={(
              <div className="flex flex-wrap items-center gap-2">
                <button
                  className="btn-secondary text-xs"
                  onClick={() => handleExport('alerts')}
                  disabled={exportingDataset !== null}
                >
                  {exportingDataset === 'alerts' ? 'Exporting Alerts...' : 'Export Alerts CSV'}
                </button>
                <button
                  className="btn-secondary text-xs"
                  onClick={() => handleExport('webhook-events')}
                  disabled={exportingDataset !== null}
                >
                  {exportingDataset === 'webhook-events' ? 'Exporting Webhooks...' : 'Export Webhooks CSV'}
                </button>
                <button
                  className="btn-secondary text-xs"
                  onClick={() => handleExport('invoice-sync-jobs')}
                  disabled={exportingDataset !== null}
                >
                  {exportingDataset === 'invoice-sync-jobs' ? 'Exporting Jobs...' : 'Export Jobs CSV'}
                </button>
              </div>
            )}
          >
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            </div>
            <div className="mt-4 text-xs text-on-surface-variant">CSV exports include the active filters and up to 500 records per dataset.</div>
            <div className="mt-4 grid gap-3 border-t border-outline pt-4 xl:grid-cols-[minmax(0,1fr)_320px]">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">Saved Investigation Views</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {savedViews.length === 0 ? (
                    <span className="text-xs text-on-surface-variant">No saved views yet.</span>
                  ) : (
                    savedViews.map((view) => (
                      <div key={view.id} className="flex items-center gap-1">
                        <button
                          className={`rounded-pill px-3 py-1 text-xs font-medium ${activeSavedViewId === view.id ? 'bg-surface-container-high text-on-surface' : 'bg-surface-container-low text-on-surface-variant'}`}
                          onClick={() => applySavedView(view)}
                        >
                          {view.name}
                        </button>
                        <button
                          className="rounded-pill bg-surface-container-low px-2 py-1 text-xs text-on-surface-variant"
                          onClick={() => deleteSavedView(view.id)}
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row xl:flex-col">
                <input
                  className="input-field text-xs"
                  placeholder="Saved view name"
                  value={savedViewName}
                  onChange={(event) => setSavedViewName(event.target.value)}
                />
                <div className="flex flex-wrap gap-2">
                  <button className="btn-secondary text-xs" onClick={saveCurrentView}>Save Current View</button>
                  <button className="btn-secondary text-xs" onClick={resetFiltersToDefaults}>Reset Filters</button>
                </div>
              </div>
            </div>
          </AdminFilterCard>

          <div className="mb-8">
            <AdminStatGrid>
              <AdminStatCard
                icon={<ShieldExclamationIcon className="h-7 w-7" />}
                label="Billing Health"
                value={healthLoading ? 'Loading...' : health?.status || 'unknown'}
                helper={healthLoading ? 'Checking billing readiness...' : `Readiness: ${health?.ready ? 'ready' : 'blocked'}`}
                tone={health?.ready ? 'success' : 'warning'}
              />
              <AdminStatCard
                icon={<BellAlertIcon className="h-7 w-7" />}
                label="Recent Alerts"
                value={historyPanelsLoading ? 'Loading...' : alertSummary.total}
                helper={healthLoading ? 'Loading alert posture...' : `Pending manual action: ${health?.alerts?.manualActionRequired || 0}`}
                tone="warning"
              />
              <AdminStatCard
                icon={<ExclamationTriangleIcon className="h-7 w-7" />}
                label="Attention Backlog"
                value={summaryLoading ? 'Loading...' : (summary?.webhook_events?.deadLetter || 0) + (summary?.invoice_sync_jobs?.deadLetter || 0)}
                helper={summaryLoading ? 'Loading durable queue backlog...' : 'Dead-letter records across recovery queues'}
                tone="danger"
              />
              <AdminStatCard
                icon={<LinkIcon className="h-7 w-7" />}
                label="Reconciliation Drift"
                value={reconciliationLoading ? 'Loading...' : reconciliation?.stripe_state_drift?.total || 0}
                helper={reconciliationLoading ? 'Loading reconciliation scan...' : 'Live Stripe mismatches in the current window'}
                tone="info"
              />
            </AdminStatGrid>
          </div>

          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr] mb-8">
            <AdminFilterCard
              title="Recovery Controls"
              description="Run manual sweeps and reconciliation repairs against the durable billing ledgers."
            >
              <div className="mb-4 flex items-center justify-between">
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <button className="btn-secondary flex items-center justify-center gap-2" onClick={() => webhookRecoveryMutation.mutate()} disabled={webhookRecoveryMutation.isLoading}>
                  <BoltIcon className="h-4 w-4" />
                  <span>Recover Webhook Events</span>
                </button>
                <button className="btn-secondary flex items-center justify-center gap-2" onClick={() => invoiceRecoveryMutation.mutate()} disabled={invoiceRecoveryMutation.isLoading}>
                  <BoltIcon className="h-4 w-4" />
                  <span>Recover Invoice Sync Jobs</span>
                </button>
                <button className="btn-secondary flex items-center justify-center gap-2" onClick={() => resyncMutation.mutate()} disabled={resyncMutation.isLoading}>
                  <CreditCardIcon className="h-4 w-4" />
                  <span>Resync Open Invoices</span>
                </button>
                <button className="btn-secondary flex items-center justify-center gap-2" onClick={() => repairMutation.mutate()} disabled={repairMutation.isLoading}>
                  <WrenchScrewdriverIcon className="h-4 w-4" />
                  <span>Repair Stripe Drift</span>
                </button>
                <button className="btn-secondary flex items-center justify-center gap-2" onClick={() => customerRepairMutation.mutate()} disabled={customerRepairMutation.isLoading}>
                  <LinkIcon className="h-4 w-4" />
                  <span>Repair Customer Mappings</span>
                </button>
                <button className="btn-secondary flex items-center justify-center gap-2" onClick={() => feeRepairMutation.mutate()} disabled={feeRepairMutation.isLoading}>
                  <WrenchScrewdriverIcon className="h-4 w-4" />
                  <span>Refresh Fee Parity</span>
                </button>
                <button className="btn-secondary flex items-center justify-center gap-2" onClick={() => disputeRepairMutation.mutate()} disabled={disputeRepairMutation.isLoading}>
                  <CheckCircleIcon className="h-4 w-4" />
                  <span>Repair Lost Disputes</span>
                </button>
              </div>
            </AdminFilterCard>

            <AdminTableCard title="Health Reasons">
              {healthLoading ? (
                <LoadingSpinner className="py-12" />
              ) : health?.reasons?.length ? (
                <ul className="mt-4 space-y-3">
                  {health.reasons.map((reason: string) => (
                    <li key={reason} className="flex items-start gap-3 text-sm text-on-surface-variant">
                      <ShieldExclamationIcon className="mt-0.5 h-4 w-4 text-orange-600" />
                      <span>{reason}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="mt-4 flex items-center gap-3 text-sm text-on-surface-variant">
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                  <span>No active billing health concerns reported.</span>
                </div>
              )}
            </AdminTableCard>
          </div>

          <div className="grid gap-6 xl:grid-cols-2 mb-8">
            <AdminTableCard title="Reconciliation Snapshot">
              {reconciliationLoading ? (
                <LoadingSpinner className="py-12" />
              ) : (
                <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-3">
                  <div className="admin-subpanel rounded-card p-4">
                    <p className="text-sm text-on-surface-variant">Missing Stripe Sync</p>
                    <p className="mt-1 text-2xl font-bold text-on-surface">{reconciliation?.invoices_missing_stripe_sync?.open_total || 0}</p>
                  </div>
                  <div className="admin-subpanel rounded-card p-4">
                    <p className="text-sm text-on-surface-variant">Stripe Drift</p>
                    <p className="mt-1 text-2xl font-bold text-on-surface">{reconciliation?.stripe_state_drift?.total || 0}</p>
                  </div>
                  <div className="admin-subpanel rounded-card p-4">
                    <p className="text-sm text-on-surface-variant">Customer Mapping Issues</p>
                    <p className="mt-1 text-2xl font-bold text-on-surface">{reconciliation?.customer_mapping_consistency?.total || 0}</p>
                  </div>
                  <div className="admin-subpanel rounded-card p-4">
                    <p className="text-sm text-on-surface-variant">Fee Consistency Issues</p>
                    <p className="mt-1 text-2xl font-bold text-on-surface">{reconciliation?.fee_consistency?.total || 0}</p>
                  </div>
                  <div className="admin-subpanel rounded-card p-4">
                    <p className="text-sm text-on-surface-variant">Webhook Completeness Gaps</p>
                    <p className="mt-1 text-2xl font-bold text-on-surface">{reconciliation?.webhook_completeness?.total || 0}</p>
                  </div>
                  <div className="admin-subpanel rounded-card p-4">
                    <p className="text-sm text-on-surface-variant">Dispute Parity Issues</p>
                    <p className="mt-1 text-2xl font-bold text-on-surface">{reconciliation?.dispute_consistency?.total || 0}</p>
                  </div>
                  <div className="admin-subpanel rounded-card p-4">
                    <p className="text-sm text-on-surface-variant">Reservation Dedupe Outcomes</p>
                    <p className="mt-1 text-2xl font-bold text-on-surface">{reconciliation?.reservation_invoice_deduplication?.total || 0}</p>
                  </div>
                </div>
              )}
            </AdminTableCard>

            <AdminTableCard title="Recovery Backlog">
              {summaryLoading || healthLoading ? (
                <LoadingSpinner className="py-12" />
              ) : (
                <div className="mt-4 space-y-4 text-sm text-on-surface-variant">
                  <div className="admin-subpanel flex items-center justify-between rounded-card p-4">
                    <span>Webhook retryable failures</span>
                    <span className="font-semibold text-on-surface">{summary?.webhook_events?.retryableFailed || 0}</span>
                  </div>
                  <div className="admin-subpanel flex items-center justify-between rounded-card p-4">
                    <span>Webhook dead-letter</span>
                    <span className="font-semibold text-on-surface">{summary?.webhook_events?.deadLetter || 0}</span>
                  </div>
                  <div className="admin-subpanel flex items-center justify-between rounded-card p-4">
                    <span>Invoice sync retryable failures</span>
                    <span className="font-semibold text-on-surface">{summary?.invoice_sync_jobs?.retryableFailed || 0}</span>
                  </div>
                  <div className="admin-subpanel flex items-center justify-between rounded-card p-4">
                    <span>Invoice sync dead-letter</span>
                    <span className="font-semibold text-on-surface">{summary?.invoice_sync_jobs?.deadLetter || 0}</span>
                  </div>
                  <div className="admin-subpanel flex items-center justify-between rounded-card p-4">
                    <span>Alert retries due now</span>
                    <span className="font-semibold text-on-surface">{health?.alerts?.dueNow || 0}</span>
                  </div>
                  <div className="admin-subpanel flex items-center justify-between rounded-card p-4">
                    <span>Alert manual action required</span>
                    <span className="font-semibold text-on-surface">{health?.alerts?.manualActionRequired || 0}</span>
                  </div>
                </div>
              )}
            </AdminTableCard>
          </div>

          <AdminTableCard
            className="mb-8"
            title="Stripe Account Health"
            description="Latest read-only Stripe readiness check for the platform and each connected tenant."
            meta={(
              <button
                className="btn-secondary flex items-center gap-2 text-xs"
                onClick={() => stripeAccountHealthSweepMutation.mutate()}
                disabled={stripeAccountHealthSweepMutation.isLoading}
              >
                <BoltIcon className="h-4 w-4" />
                <span>{stripeAccountHealthSweepMutation.isLoading ? 'Checking Accounts...' : 'Check All Accounts'}</span>
              </button>
            )}
          >
            {stripeAccountHealthLoading ? (
              <LoadingSpinner className="py-12" />
            ) : !stripeAccountHealth?.accounts?.length ? (
              <div className="px-6 py-10 text-sm text-on-surface-variant">No account health snapshots have been recorded. Run the first check to establish the baseline.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="admin-table-grid min-w-full">
                  <thead className="admin-table-head">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Account</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Health</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Charges</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Payouts</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Reason</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Checked</th>
                    </tr>
                  </thead>
                  <tbody className="admin-table-body">
                    {stripeAccountHealth.accounts.map((account: any) => (
                      <tr key={account.id} className="admin-table-row">
                        <td className="px-6 py-4 text-sm font-medium text-on-surface">
                          {account.company_name || 'Unknown account'}
                          <div className="mt-1 text-xs font-normal text-on-surface-variant">{account.account_scope}</div>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-on-surface">{account.effective_status}</td>
                        <td className="px-6 py-4 text-sm text-on-surface">{account.charges_enabled == null ? 'Unknown' : account.charges_enabled ? 'Enabled' : 'Disabled'}</td>
                        <td className="px-6 py-4 text-sm text-on-surface">{account.payouts_enabled == null ? 'Unknown' : account.payouts_enabled ? 'Enabled' : 'Disabled'}</td>
                        <td className="max-w-md px-6 py-4 text-sm text-on-surface-variant">{account.status_reasons?.join(', ') || 'No issues reported'}</td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-on-surface-variant">{new Date(account.checked_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </AdminTableCard>

          <AdminTableCard
            className="mb-8"
            title="Collected Fee Activity"
            description="Recent paid invoices and corresponding collected platform fees for the selected scope."
          >
            {!historyPanelsEnabled || paidInvoicesActivityLoading ? (
              <LoadingSpinner className="py-12" />
            ) : collectedFeeActivity.length === 0 ? (
              <div className="px-6 py-10 text-sm text-on-surface-variant">No collected fee records found for the current filters.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="admin-table-grid min-w-full">
                  <thead className="admin-table-head">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Invoice</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Company</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Collected Fee</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Paid Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Stripe Reference</th>
                    </tr>
                  </thead>
                  <tbody className="admin-table-body">
                    {collectedFeeActivity.map((entry: any) => (
                      <tr key={entry.id} className="admin-table-row">
                        <td className="px-6 py-4 text-sm text-on-surface">{entry.paidAt ? entry.paidAt.toLocaleString() : 'N/A'}</td>
                        <td className="px-6 py-4 text-sm font-medium text-on-surface">
                          <button
                            type="button"
                            className="text-left hover:underline hover:text-primary-600"
                            onClick={() => openInvoiceDetails(entry.id)}
                          >
                            #{entry.invoiceNumber}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm text-on-surface">{entry.companyName}</td>
                        <td className="px-6 py-4 text-sm text-on-surface">
                          <button
                            type="button"
                            className="text-left hover:underline hover:text-primary-600"
                            onClick={() => openCustomerDrawer(entry)}
                          >
                            {entry.customerEmail}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-amber-700">{formatUsd(entry.feeUsd)}</td>
                        <td className="px-6 py-4 text-sm text-on-surface">{formatUsd(entry.totalUsd)}</td>
                        <td className="px-6 py-4 text-xs text-on-surface-variant">
                          {entry.stripeReference ? (
                            <div className="max-w-[18rem]">
                              <div className="font-mono text-on-surface">{entry.stripeReference}</div>
                              <div className="mt-1 text-[11px] uppercase tracking-wide text-on-surface-variant">
                                {entry.groupedPaymentRequestId
                                  ? `Grouped ${entry.stripeReferenceType === 'payment_intent' ? 'payment intent' : 'invoice'}`
                                  : entry.stripeReferenceType === 'payment_intent'
                                    ? 'Payment intent'
                                    : 'Invoice'}
                              </div>
                            </div>
                          ) : (
                            'N/A'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </AdminTableCard>

          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr] mb-8">
            {reconciliationLoading ? (
              <AdminTableCard className="xl:col-span-2" title="Reconciliation Detail">
                <LoadingSpinner className="py-20" />
              </AdminTableCard>
            ) : (
              <>
            <div className="grid gap-6 xl:grid-cols-2 mb-8 xl:col-span-2">
              <div className="card overflow-hidden">
                <div className="border-b border-outline px-6 py-4">
                  <h2 className="text-lg font-semibold text-on-surface">Webhook Completeness</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-outline">
                    <thead className="bg-surface-container-low">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Invoice</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Issue</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Observed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline bg-surface-container-lowest">
                      {(reconciliation?.webhook_completeness?.candidates || []).slice(0, 6).map((candidate: any) => (
                        <tr key={`${candidate.invoice_id}-${candidate.issue_type}`}>
                          <td className="px-6 py-4 text-sm text-on-surface">
                            <div className="font-medium">{candidate.invoice_number}</div>
                            <div className="text-xs text-on-surface-variant">{candidate.company_name || candidate.company_id}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-on-surface-variant">{candidate.issue_type.replaceAll('_', ' ')}</td>
                          <td className="px-6 py-4 text-sm text-on-surface-variant">
                            {candidate.observed_event_status ? `${candidate.observed_event_type || 'event'} / ${candidate.observed_event_status}` : 'missing'}
                          </td>
                        </tr>
                      ))}
                      {(!reconciliation?.webhook_completeness?.candidates || reconciliation.webhook_completeness.candidates.length === 0) && (
                        <tr>
                          <td colSpan={3} className="px-6 py-8 text-center text-sm text-on-surface-variant">No webhook completeness gaps in the current window.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="card overflow-hidden">
                <div className="border-b border-outline px-6 py-4">
                  <h2 className="text-lg font-semibold text-on-surface">Dispute Parity</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-outline">
                    <thead className="bg-surface-container-low">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Dispute</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Issue</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Repair</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline bg-surface-container-lowest">
                      {(reconciliation?.dispute_consistency?.candidates || []).slice(0, 6).map((candidate: any) => (
                        <tr key={`${candidate.dispute_id}-${candidate.issue_type}`}>
                          <td className="px-6 py-4 text-sm text-on-surface">
                            <div className="font-medium">{candidate.stripe_dispute_id}</div>
                            <div className="text-xs text-on-surface-variant">{candidate.invoice_number || 'No linked invoice'}</div>
                            {!candidate.invoice_number && candidate.suggested_invoice_number && (
                              <div className="text-xs text-on-surface-variant">Suggested {candidate.suggested_invoice_number}</div>
                            )}
                            <div className="text-xs text-on-surface-variant">{candidate.company_name || candidate.company_id}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-on-surface-variant">
                            <div>{candidate.issue_type.replaceAll('_', ' ')}</div>
                            <div className="text-xs">status {candidate.dispute_status}{candidate.invoice_status ? ` / invoice ${candidate.invoice_status}` : ''}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-on-surface-variant">
                            <div>{candidate.repair_action.replaceAll('_', ' ')}</div>
                            {candidate.repair_action === 'link_invoice' && candidate.suggested_invoice_number && (
                              <div className="mt-1 text-xs">Will link {candidate.suggested_invoice_number}</div>
                            )}
                            {candidate.repair_action === 'manual_review' && (
                              <>
                                <div className="mt-1 text-xs">Workflow {candidate.manual_review_status || 'open'}</div>
                                {candidate.manual_review_note && <div className="mt-1 text-xs">{candidate.manual_review_note}</div>}
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {candidate.manual_review_status !== 'reviewed' && (
                                    <button
                                      className="btn-secondary text-xs"
                                      onClick={() => handleReviewDisputeCandidate(candidate, 'reviewed')}
                                      disabled={reviewDisputeCandidateMutation.isLoading}
                                    >
                                      Mark Reviewed
                                    </button>
                                  )}
                                  <button
                                    className="btn-secondary text-xs"
                                    onClick={() => handleReviewDisputeCandidate(candidate, 'resolved')}
                                    disabled={reviewDisputeCandidateMutation.isLoading}
                                  >
                                    Resolve
                                  </button>
                                </div>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                      {(!reconciliation?.dispute_consistency?.candidates || reconciliation.dispute_consistency.candidates.length === 0) && (
                        <tr>
                          <td colSpan={3} className="px-6 py-8 text-center text-sm text-on-surface-variant">No dispute parity gaps in the current window.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="card overflow-hidden">
                <div className="border-b border-outline px-6 py-4">
                  <h2 className="text-lg font-semibold text-on-surface">Reservation Dedupe Outcomes</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-outline">
                    <thead className="bg-surface-container-low">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Invoice</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Archived Reservation</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Canonical Link</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline bg-surface-container-lowest">
                      {(reconciliation?.reservation_invoice_deduplication?.candidates || []).slice(0, 6).map((candidate: any) => (
                        <tr key={candidate.invoice_id}>
                          <td className="px-6 py-4 text-sm text-on-surface">
                            <div className="font-medium">{candidate.invoice_number}</div>
                            <div className="text-xs text-on-surface-variant">{candidate.company_name || candidate.company_id}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-on-surface-variant">
                            <div>{candidate.archived_reservation_id || 'unknown'}</div>
                            <div className="text-xs">Recorded {candidate.deduped_at ? formatDateTime(candidate.deduped_at) : 'unknown'}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-on-surface-variant">
                            {candidate.kept_invoice_id || 'manual review'}
                          </td>
                        </tr>
                      ))}
                      {(!reconciliation?.reservation_invoice_deduplication?.candidates || reconciliation.reservation_invoice_deduplication.candidates.length === 0) && (
                        <tr>
                          <td colSpan={3} className="px-6 py-8 text-center text-sm text-on-surface-variant">No reservation dedupe outcomes in the current window.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="card overflow-hidden">
                <div className="border-b border-outline px-6 py-4">
                  <h2 className="text-lg font-semibold text-on-surface">Customer Mapping Candidates</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-outline">
                    <thead className="bg-surface-container-low">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Invoice</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Issue</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Repair</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline bg-surface-container-lowest">
                      {(reconciliation?.customer_mapping_consistency?.candidates || []).slice(0, 6).map((candidate: any) => (
                        <tr key={`${candidate.invoice_id}-${candidate.issue_type}`}>
                          <td className="px-6 py-4 text-sm text-on-surface">
                            <div className="font-medium">{candidate.invoice_number}</div>
                            <div className="text-xs text-on-surface-variant">{candidate.company_name || candidate.company_id}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-on-surface-variant">{candidate.issue_type.replaceAll('_', ' ')}</td>
                          <td className="px-6 py-4 text-sm text-on-surface-variant">{candidate.repair_action.replaceAll('_', ' ')}</td>
                        </tr>
                      ))}
                      {(!reconciliation?.customer_mapping_consistency?.candidates || reconciliation.customer_mapping_consistency.candidates.length === 0) && (
                        <tr>
                          <td colSpan={3} className="px-6 py-8 text-center text-sm text-on-surface-variant">No connected-account customer mapping issues in the current window.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="card overflow-hidden">
                <div className="border-b border-outline px-6 py-4">
                  <h2 className="text-lg font-semibold text-on-surface">Fee Parity Candidates</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-outline">
                    <thead className="bg-surface-container-low">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Invoice</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Issue</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Expected</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline bg-surface-container-lowest">
                      {(reconciliation?.fee_consistency?.candidates || []).slice(0, 6).map((candidate: any) => (
                        <tr key={`${candidate.invoice_id}-${candidate.issue_type}`}>
                          <td className="px-6 py-4 text-sm text-on-surface">
                            <div className="font-medium">{candidate.invoice_number}</div>
                            <div className="text-xs text-on-surface-variant">{candidate.company_name || candidate.company_id}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-on-surface-variant">{candidate.issue_type.replaceAll('_', ' ')}</td>
                          <td className="px-6 py-4 text-sm text-on-surface-variant">
                            {candidate.expected_platform_fee != null || candidate.expected_stripe_fee != null
                              ? `platform ${candidate.expected_platform_fee ?? 'n/a'} / stripe ${candidate.expected_stripe_fee ?? 'n/a'}`
                              : 'manual review'}
                          </td>
                        </tr>
                      ))}
                      {(!reconciliation?.fee_consistency?.candidates || reconciliation.fee_consistency.candidates.length === 0) && (
                        <tr>
                          <td colSpan={3} className="px-6 py-8 text-center text-sm text-on-surface-variant">No Stripe fee parity mismatches in the current window.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="card overflow-hidden">
              <div className="border-b border-outline px-6 py-4">
                <h2 className="text-lg font-semibold text-on-surface">Stripe Drift Candidates</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-outline">
                  <thead className="bg-surface-container-low">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Invoice</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Drift</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Repair</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline bg-surface-container-lowest">
                    {(reconciliation?.stripe_state_drift?.candidates || []).slice(0, 8).map((candidate: any) => (
                      <tr key={`${candidate.invoice_id}-${candidate.drift_type}`}>
                        <td className="px-6 py-4 text-sm text-on-surface">
                          <div className="font-medium">{candidate.invoice_number}</div>
                          <div className="text-xs text-on-surface-variant">{candidate.company_name || candidate.company_id}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-on-surface-variant">{candidate.drift_type.replaceAll('_', ' ')}</td>
                        <td className="px-6 py-4 text-sm text-on-surface-variant">{candidate.repair_action.replaceAll('_', ' ')}</td>
                      </tr>
                    ))}
                    {(!reconciliation?.stripe_state_drift?.candidates || reconciliation.stripe_state_drift.candidates.length === 0) && (
                      <tr>
                        <td colSpan={3} className="px-6 py-8 text-center text-sm text-on-surface-variant">No live Stripe drift candidates in the current window.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
              </>
            )}

            <div className="card overflow-hidden">
              <div className="border-b border-outline px-6 py-4">
                <div className="flex items-center justify-between gap-4">
                  <h2 className="text-lg font-semibold text-on-surface">Recent Alerts</h2>
                  <button
                    className="btn-secondary text-xs"
                    onClick={() => retryFailedAlertsMutation.mutate()}
                    disabled={retryFailedAlertsMutation.isLoading}
                  >
                    Retry Failed Alerts
                  </button>
                </div>
                <div className="mt-2 text-xs text-on-surface-variant">Window: {historyWindowLabel}</div>
                <div className="mt-4 grid gap-3 md:grid-cols-4">
                  <select
                    className="input-field text-xs"
                    value={alertTargetFilter}
                    onChange={(event) => {
                      setAlertTargetFilter(event.target.value);
                      setSelectedAlertId(null);
                      setAlertPage(1);
                      setActiveSavedViewId(null);
                    }}
                  >
                    <option value="">All alert targets</option>
                    <option value="webhook_events">Webhook events</option>
                    <option value="invoice_sync_jobs">Invoice sync jobs</option>
                  </select>
                  <select
                    className="input-field text-xs"
                    value={alertSeverityFilter}
                    onChange={(event) => {
                      setAlertSeverityFilter(event.target.value);
                      setSelectedAlertId(null);
                      setAlertPage(1);
                      setActiveSavedViewId(null);
                    }}
                  >
                    <option value="">All severities</option>
                    <option value="warning">Warning</option>
                    <option value="error">Error</option>
                  </select>
                  <select
                    className="input-field text-xs"
                    value={alertDeliveryStatusFilter}
                    onChange={(event) => {
                      setAlertDeliveryStatusFilter(event.target.value);
                      setSelectedAlertId(null);
                      setAlertPage(1);
                      setActiveSavedViewId(null);
                    }}
                  >
                    <option value="">All delivery states</option>
                    <option value="failed">Failed</option>
                    <option value="partial">Partial</option>
                    <option value="sent">Sent</option>
                    <option value="suppressed">Suppressed</option>
                    <option value="unconfigured">Unconfigured</option>
                  </select>
                  <select
                    className="input-field text-xs"
                    value={alertWorkflowStatusFilter}
                    onChange={(event) => {
                      setAlertWorkflowStatusFilter(event.target.value);
                      setSelectedAlertId(null);
                      setAlertPage(1);
                      setActiveSavedViewId(null);
                    }}
                  >
                    <option value="">All workflow states</option>
                    <option value="open">Open</option>
                    <option value="acknowledged">Acknowledged</option>
                    <option value="handed_off">Handed off</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-6">
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Matching alerts</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{alertSummary.total}</div>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Visible on page</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{alertSummary.visible}</div>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Failed delivery</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{alertSummary.failed}</div>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Manual action</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{alertSummary.manualAction}</div>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Unacknowledged</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{alertSummary.unacknowledged}</div>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Handed off</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{alertSummary.handedOff}</div>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Resolved</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{alertSummary.resolved}</div>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-outline">
                {!historyPanelsEnabled || alertsLoading ? (
                  <LoadingSpinner className="py-12" />
                ) : alerts.length === 0 ? (
                  <div className="px-6 py-8 text-sm text-on-surface-variant">No billing alerts recorded in the selected history window.</div>
                ) : (
                  alerts.map((alert: any) => (
                    <div
                      key={alert.id}
                      className={`px-6 py-4 ${selectedAlertId === alert.id ? 'bg-surface-container-low' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-medium text-on-surface">{alert.subject || alert.alert_type}</div>
                          <div className="mt-1 text-xs text-on-surface-variant">{alert.target || 'billing'} · {formatDateTime(alert.created_at)}</div>
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          <span className={`rounded-pill px-2 py-1 text-xs font-semibold uppercase ${workflowTone(alert.workflow_status)}`}>
                            {formatLabel(alert.workflow_status)}
                          </span>
                          <span className={`rounded-pill px-2 py-1 text-xs font-semibold uppercase ${severityTone(alert.severity)}`}>
                            {alert.severity}
                          </span>
                          <span className={`rounded-pill px-2 py-1 text-xs font-semibold uppercase ${deliveryTone(alert.delivery_status)}`}>
                            {alert.delivery_status}
                          </span>
                        </div>
                      </div>
                      <p className="mt-3 text-sm text-on-surface-variant line-clamp-3 [line-clamp:3]">{alert.message}</p>
                      <div className="mt-3 flex flex-wrap gap-2 text-xs text-on-surface-variant">
                        <span>Retries: {alert.retry_count || 0}</span>
                        <span>Deliveries: {alert.deliveries?.length || 0}</span>
                        <span>Acknowledged: {alert.acknowledged_at ? formatDateTime(alert.acknowledged_at) : 'No'}</span>
                        <span>Handed off: {alert.handed_off_at ? formatDateTime(alert.handed_off_at) : 'No'}</span>
                        <span>Resolved: {alert.resolved_at ? formatDateTime(alert.resolved_at) : 'No'}</span>
                        <span>Escalation: {formatLabel(alert.metadata?.alert_delivery?.escalation_state)}</span>
                      </div>
                      {Array.isArray(alert.metadata?.alert_delivery?.operator_guidance) && alert.metadata.alert_delivery.operator_guidance.length > 0 && (
                        <div className="mt-2 rounded-card bg-surface-container-low px-3 py-2 text-xs text-on-surface-variant">
                          {(alert.metadata.alert_delivery.operator_guidance as string[]).join(' ')}
                        </div>
                      )}
                      {alert.acknowledgement_note && (
                        <div className="mt-2 rounded-card bg-surface-container-low px-3 py-2 text-xs text-on-surface-variant">
                          {alert.acknowledgement_note}
                        </div>
                      )}
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          className="btn-secondary text-xs"
                          onClick={() => setSelectedAlertId(selectedAlertId === alert.id ? null : alert.id)}
                        >
                          {selectedAlertId === alert.id ? 'Hide Details' : 'View Details'}
                        </button>
                        <button
                          className="btn-secondary text-xs"
                          onClick={() => handleAcknowledgeAlert(alert)}
                          disabled={acknowledgeAlertMutation.isLoading || alert.workflow_status === 'resolved'}
                        >
                          {alert.acknowledged_at ? 'Update Acknowledgement' : 'Acknowledge'}
                        </button>
                        <button
                          className="btn-secondary text-xs"
                          onClick={() => handleHandoffAlert(alert)}
                          disabled={handoffAlertMutation.isLoading || alert.workflow_status === 'resolved'}
                        >
                          {alert.handed_off_at ? 'Update Handoff' : 'Hand Off'}
                        </button>
                        <button
                          className="btn-secondary text-xs"
                          onClick={() => handleResolveAlert(alert)}
                          disabled={resolveAlertMutation.isLoading || alert.workflow_status === 'resolved'}
                        >
                          {alert.workflow_status === 'resolved' ? 'Resolved' : 'Resolve'}
                        </button>
                        <button
                          className="btn-secondary text-xs"
                          onClick={() => retryAlertMutation.mutate(alert.id)}
                          disabled={retryAlertMutation.isLoading || !alert.deliveries?.length || alert.workflow_status === 'resolved'}
                        >
                          Retry Delivery
                        </button>
                      </div>
                      {selectedAlertId === alert.id && (
                        <div className="mt-4 rounded-card bg-surface-container-low p-4 text-xs text-on-surface-variant">
                          {selectedAlertLoading ? (
                            <div>Loading alert delivery detail...</div>
                          ) : selectedAlert ? (
                            <div className="space-y-3">
                              <div className="grid gap-3 md:grid-cols-2">
                                <div>
                                  <div className="font-semibold text-on-surface">Workflow Status</div>
                                  <div>{formatLabel(selectedAlert.workflow_status)}</div>
                                </div>
                                <div>
                                  <div className="font-semibold text-on-surface">Escalation State</div>
                                  <div>{formatLabel(selectedAlert.metadata?.alert_delivery?.escalation_state)}</div>
                                </div>
                                <div>
                                  <div className="font-semibold text-on-surface">Routing Stage</div>
                                  <div>{formatLabel(selectedAlert.metadata?.alert_delivery?.routing_stage)}</div>
                                </div>
                                <div>
                                  <div className="font-semibold text-on-surface">Last Retry</div>
                                  <div>{formatDateTime(selectedAlert.last_retried_at)}</div>
                                </div>
                                <div>
                                  <div className="font-semibold text-on-surface">Channel Backoff</div>
                                  <div>
                                    Email {selectedAlert.metadata?.alert_delivery?.channel_retry_intervals?.email || '-'}m
                                    {' '}· Webhook {selectedAlert.metadata?.alert_delivery?.channel_retry_intervals?.webhook || '-'}m
                                  </div>
                                </div>
                              </div>
                              <div className="grid gap-3 md:grid-cols-2">
                                <div>
                                  <div className="font-semibold text-on-surface">Acknowledged</div>
                                  <div>{selectedAlert.acknowledged_at ? `${formatDateTime(selectedAlert.acknowledged_at)} by ${selectedAlert.acknowledged_by || 'unknown'}` : 'No'}</div>
                                  {selectedAlert.acknowledgement_note && <div className="mt-1">{selectedAlert.acknowledgement_note}</div>}
                                </div>
                                <div>
                                  <div className="font-semibold text-on-surface">Resolved</div>
                                  <div>{selectedAlert.resolved_at ? `${formatDateTime(selectedAlert.resolved_at)} by ${selectedAlert.resolved_by || 'unknown'}` : 'No'}</div>
                                  {selectedAlert.resolution_note && <div className="mt-1">{selectedAlert.resolution_note}</div>}
                                </div>
                              </div>
                              <div>
                                <div className="font-semibold text-on-surface">External Handoff</div>
                                {selectedAlert.handed_off_at ? (
                                  <div className="mt-2 rounded-card bg-surface px-3 py-2">
                                    <div>Provider: {selectedAlert.handoff_provider || 'unknown'}</div>
                                    <div>Reference: {selectedAlert.handoff_reference || 'Not recorded'}</div>
                                    <div>Handed off: {formatDateTime(selectedAlert.handed_off_at)} by {selectedAlert.handed_off_by || 'unknown'}</div>
                                    {selectedAlert.handoff_note && <div className="mt-1">{selectedAlert.handoff_note}</div>}
                                  </div>
                                ) : (
                                  <div className="mt-2">No external handoff recorded.</div>
                                )}
                              </div>
                              <div>
                                <div className="font-semibold text-on-surface">Routing Targets</div>
                                <div className="mt-2 space-y-2">
                                  {Array.isArray(selectedAlert.metadata?.alert_delivery?.routing_targets) && selectedAlert.metadata.alert_delivery.routing_targets.length > 0 ? (
                                    selectedAlert.metadata.alert_delivery.routing_targets.map((target: any, index: number) => (
                                      <div key={`${target.destination}-${index}`} className="rounded-card bg-surface px-3 py-2">
                                        <div className="font-medium text-on-surface">{target.channel} · {target.destination}</div>
                                        <div className="mt-1">Route level: {formatLabel(target.route_level)}</div>
                                      </div>
                                    ))
                                  ) : (
                                    <div>No routing targets recorded.</div>
                                  )}
                                </div>
                              </div>
                              <div>
                                <div className="font-semibold text-on-surface">Recorded Deliveries</div>
                                <div className="mt-2 space-y-2">
                                  {(selectedAlert.deliveries || []).length === 0 ? (
                                    <div>No delivery records captured.</div>
                                  ) : (
                                    selectedAlert.deliveries.map((delivery: any, index: number) => (
                                      <div key={`${delivery.destination}-${index}`} className="rounded-card bg-surface px-3 py-2">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                          <span className="font-medium text-on-surface">{delivery.channel} · {delivery.destination}</span>
                                          <span>{delivery.success ? 'success' : 'failed'}</span>
                                        </div>
                                        <div className="mt-1">Attempted: {formatDateTime(delivery.attempted_at)}</div>
                                        {delivery.retry_of && <div>Retry of alert: {delivery.retry_of}</div>}
                                        {delivery.statusCode && <div>Status code: {delivery.statusCode}</div>}
                                        {delivery.error && <div>Error: {delivery.error}</div>}
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div>Alert detail unavailable.</div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
              {!historyPanelsEnabled || alertsLoading ? null : renderPaginationControls({
                page: alertsResponse?.page || 1,
                pageSize: alertsResponse?.pageSize || limit,
                total: alertsResponse?.total || 0,
                totalPages: alertsResponse?.totalPages || 1,
                currentCount: alerts.length,
                onPageChange: setAlertPage,
              })}
            </div>
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <div className="card overflow-hidden">
              <div className="border-b border-outline px-6 py-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <h2 className="text-lg font-semibold text-on-surface">Recent Webhook Events</h2>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      className="input-field text-xs"
                      placeholder="Filter event type"
                      value={webhookEventTypeFilter}
                      onChange={(event) => {
                        setWebhookEventTypeFilter(event.target.value);
                        setSelectedWebhookEventIds([]);
                        setWebhookEventsPage(1);
                        setActiveSavedViewId(null);
                      }}
                    />
                    <select
                      className="input-field text-xs"
                      value={webhookStatusFilter}
                      onChange={(event) => {
                        setWebhookStatusFilter(event.target.value);
                        setSelectedWebhookEventIds([]);
                        setWebhookEventsPage(1);
                        setActiveSavedViewId(null);
                      }}
                    >
                      <option value="">All statuses</option>
                      <option value="failed">Failed</option>
                      <option value="processing">Processing</option>
                      <option value="pending">Pending</option>
                      <option value="dead_letter">Dead letter</option>
                      <option value="processed">Processed</option>
                    </select>
                    <button
                      className="btn-secondary text-xs"
                      onClick={() => handleExport('webhook-events')}
                      disabled={exportingDataset !== null}
                    >
                      {exportingDataset === 'webhook-events' ? 'Exporting...' : 'Export CSV'}
                    </button>
                    <button
                      className="btn-secondary text-xs"
                      onClick={() => toggleVisibleSelection(visibleWebhookEventIds, selectedWebhookEventIds, setSelectedWebhookEventIds)}
                      disabled={visibleWebhookEventIds.length === 0}
                    >
                      {visibleWebhookEventIds.length > 0 && visibleWebhookEventIds.every((id: string) => selectedWebhookEventIds.includes(id)) ? 'Clear Visible' : 'Select Visible'}
                    </button>
                    <button
                      className="btn-secondary text-xs"
                      onClick={() => bulkReplayWebhookMutation.mutate(selectedWebhookEventIds)}
                      disabled={bulkReplayWebhookMutation.isLoading || selectedWebhookEventIds.length === 0}
                    >
                      Replay Selected ({selectedWebhookEventIds.length})
                    </button>
                  </div>
                </div>
                <div className="mt-2 text-xs text-on-surface-variant">Window: {historyWindowLabel}</div>
                <div className="mt-4 grid gap-3 md:grid-cols-4">
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Matching events</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{webhookSummary.total}</div>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Visible on page</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{webhookSummary.visible}</div>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Failed</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{webhookSummary.failed}</div>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Dead letter</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{webhookSummary.deadLetter}</div>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Processing</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{webhookSummary.processing}</div>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-outline">
                {!historyPanelsEnabled || webhookEventsLoading ? (
                  <LoadingSpinner className="py-12" />
                ) : webhookEvents.length === 0 ? (
                  <div className="px-6 py-8 text-sm text-on-surface-variant">No durable webhook events found in the selected history window.</div>
                ) : (
                  webhookEvents.map((event: any) => (
                    <div key={event.id} className="px-6 py-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <label className="mb-3 flex items-center gap-2 text-xs text-on-surface-variant">
                            <input
                              type="checkbox"
                              checked={selectedWebhookEventIds.includes(event.id)}
                              onChange={() => toggleSelection(event.id, selectedWebhookEventIds, setSelectedWebhookEventIds)}
                            />
                            Select for bulk replay
                          </label>
                          <div className="text-sm font-medium text-on-surface">{event.event_type}</div>
                          <div className="mt-1 text-xs text-on-surface-variant">{event.stripe_event_id} · {formatDateTime(event.updated_at)}</div>
                        </div>
                        <span className={`rounded-pill px-2 py-1 text-xs font-semibold uppercase ${deliveryTone(event.status)}`}>
                          {formatLabel(event.status)}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-xs text-on-surface-variant">
                        <span>Attempts: {event.attempts || 0}</span>
                        <span>Account: {event.stripe_account_id || 'platform'}</span>
                      </div>
                      {event.last_error && (
                        <div className="mt-2 rounded-card bg-surface-container-low px-3 py-2 text-xs text-on-surface-variant">
                          {event.last_error}
                        </div>
                      )}
                      <div className="mt-4">
                        <button
                          className="btn-secondary text-xs"
                          onClick={() => replayWebhookMutation.mutate(event.id)}
                          disabled={replayWebhookMutation.isLoading}
                        >
                          Replay Event
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {!historyPanelsEnabled || webhookEventsLoading ? null : renderPaginationControls({
                page: webhookEventsResponse?.page || 1,
                pageSize: webhookEventsResponse?.pageSize || limit,
                total: webhookEventsResponse?.total || 0,
                totalPages: webhookEventsResponse?.totalPages || 1,
                currentCount: webhookEvents.length,
                onPageChange: setWebhookEventsPage,
              })}
            </div>

            <div className="card overflow-hidden">
              <div className="border-b border-outline px-6 py-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <h2 className="text-lg font-semibold text-on-surface">Recent Invoice Sync Jobs</h2>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      className="input-field text-xs"
                      placeholder="Filter invoice id"
                      value={invoiceIdFilter}
                      onChange={(event) => {
                        setInvoiceIdFilter(event.target.value);
                        setSelectedInvoiceSyncJobIds([]);
                        setInvoiceSyncJobsPage(1);
                        setActiveSavedViewId(null);
                      }}
                    />
                    <select
                      className="input-field text-xs"
                      value={invoiceJobStatusFilter}
                      onChange={(event) => {
                        setInvoiceJobStatusFilter(event.target.value);
                        setSelectedInvoiceSyncJobIds([]);
                        setInvoiceSyncJobsPage(1);
                        setActiveSavedViewId(null);
                      }}
                    >
                      <option value="">All statuses</option>
                      <option value="failed">Failed</option>
                      <option value="processing">Processing</option>
                      <option value="pending">Pending</option>
                      <option value="dead_letter">Dead letter</option>
                      <option value="completed">Completed</option>
                    </select>
                    <button
                      className="btn-secondary text-xs"
                      onClick={() => handleExport('invoice-sync-jobs')}
                      disabled={exportingDataset !== null}
                    >
                      {exportingDataset === 'invoice-sync-jobs' ? 'Exporting...' : 'Export CSV'}
                    </button>
                    <button
                      className="btn-secondary text-xs"
                      onClick={() => toggleVisibleSelection(visibleInvoiceSyncJobIds, selectedInvoiceSyncJobIds, setSelectedInvoiceSyncJobIds)}
                      disabled={visibleInvoiceSyncJobIds.length === 0}
                    >
                      {visibleInvoiceSyncJobIds.length > 0 && visibleInvoiceSyncJobIds.every((id: string) => selectedInvoiceSyncJobIds.includes(id)) ? 'Clear Visible' : 'Select Visible'}
                    </button>
                    <button
                      className="btn-secondary text-xs"
                      onClick={() => bulkRetryInvoiceSyncJobMutation.mutate(selectedInvoiceSyncJobIds)}
                      disabled={bulkRetryInvoiceSyncJobMutation.isLoading || selectedInvoiceSyncJobIds.length === 0}
                    >
                      Retry Selected ({selectedInvoiceSyncJobIds.length})
                    </button>
                  </div>
                </div>
                <div className="mt-2 text-xs text-on-surface-variant">Window: {historyWindowLabel}</div>
                <div className="mt-4 grid gap-3 md:grid-cols-4">
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Matching jobs</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{invoiceJobSummary.total}</div>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Visible on page</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{invoiceJobSummary.visible}</div>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Failed</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{invoiceJobSummary.failed}</div>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Dead letter</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{invoiceJobSummary.deadLetter}</div>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3 text-xs text-on-surface-variant">
                    <div>Processing</div>
                    <div className="mt-1 text-lg font-semibold text-on-surface">{invoiceJobSummary.processing}</div>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-outline">
                {!historyPanelsEnabled || invoiceSyncJobsLoading ? (
                  <LoadingSpinner className="py-12" />
                ) : invoiceSyncJobs.length === 0 ? (
                  <div className="px-6 py-8 text-sm text-on-surface-variant">No invoice sync jobs found in the selected history window.</div>
                ) : (
                  invoiceSyncJobs.map((job: any) => (
                    <div key={job.id} className="px-6 py-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <label className="mb-3 flex items-center gap-2 text-xs text-on-surface-variant">
                            <input
                              type="checkbox"
                              checked={selectedInvoiceSyncJobIds.includes(job.id)}
                              onChange={() => toggleSelection(job.id, selectedInvoiceSyncJobIds, setSelectedInvoiceSyncJobIds)}
                            />
                            Select for bulk retry
                          </label>
                          <div className="text-sm font-medium text-on-surface">{job.invoice_id}</div>
                          <div className="mt-1 text-xs text-on-surface-variant">{job.company_id} · {formatDateTime(job.updated_at)}</div>
                        </div>
                        <span className={`rounded-pill px-2 py-1 text-xs font-semibold uppercase ${deliveryTone(job.status)}`}>
                          {formatLabel(job.status)}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-xs text-on-surface-variant">
                        <span>Attempts: {job.attempts || 0}</span>
                        <span>Started: {formatDateTime(job.started_at)}</span>
                      </div>
                      {job.last_error && (
                        <div className="mt-2 rounded-card bg-surface-container-low px-3 py-2 text-xs text-on-surface-variant">
                          {job.last_error}
                        </div>
                      )}
                      <div className="mt-4">
                        <button
                          className="btn-secondary text-xs"
                          onClick={() => retryInvoiceSyncJobMutation.mutate(job.id)}
                          disabled={retryInvoiceSyncJobMutation.isLoading}
                        >
                          Retry Job
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {!historyPanelsEnabled || invoiceSyncJobsLoading ? null : renderPaginationControls({
                page: invoiceSyncJobsResponse?.page || 1,
                pageSize: invoiceSyncJobsResponse?.pageSize || limit,
                total: invoiceSyncJobsResponse?.total || 0,
                totalPages: invoiceSyncJobsResponse?.totalPages || 1,
                currentCount: invoiceSyncJobs.length,
                onPageChange: setInvoiceSyncJobsPage,
              })}
            </div>
          </div>

          {selectedFeeInvoice && (
            <AdminModalShell
              title="Invoice Details"
              description="Live fee activity details for the selected paid invoice."
              onClose={() => setSelectedFeeInvoice(null)}
              maxWidthClassName="max-w-2xl"
            >
                <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                  <div>
                    <div className="text-on-surface-variant">Invoice</div>
                    <div className="font-semibold text-on-surface">#{selectedFeeInvoice.invoiceNumber}</div>
                  </div>
                  <div>
                    <div className="text-on-surface-variant">Company</div>
                    <div className="text-on-surface">{selectedFeeInvoice.companyName}</div>
                  </div>
                  <div>
                    <div className="text-on-surface-variant">Customer</div>
                    <div className="text-on-surface">{selectedFeeInvoice.customerEmail}</div>
                  </div>
                  <div>
                    <div className="text-on-surface-variant">Paid At</div>
                    <div className="text-on-surface">{selectedFeeInvoice.paidAt ? selectedFeeInvoice.paidAt.toLocaleString() : 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-on-surface-variant">Collected Fee</div>
                    <div className="font-semibold text-amber-700">{formatUsd(selectedFeeInvoice.feeUsd)}</div>
                  </div>
                  <div>
                    <div className="text-on-surface-variant">Paid Amount</div>
                    <div className="text-on-surface">{formatUsd(selectedFeeInvoice.totalUsd)}</div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-on-surface-variant">Stripe Invoice</div>
                    <div className="font-mono text-xs text-on-surface">{selectedFeeInvoice.stripeInvoiceId || 'N/A'}</div>
                  </div>
                </div>
            </AdminModalShell>
          )}

          <CustomerDetailDrawer
            open={Boolean(selectedFeeCustomer)}
            onClose={() => setSelectedFeeCustomer(null)}
            customer={selectedFeeCustomer}
            customerDetails={selectedFeeCustomerDetailsResponse || null}
            isLoading={selectedFeeCustomerDetailsLoading}
            fallbackInvoices={selectedCustomerFeeEvents.map((entry: any) => ({
              id: entry.id,
              invoice_number: entry.invoiceNumber,
              status: 'paid',
              total: Math.round((entry.totalUsd || 0) * 100),
              amount_paid: Math.round((entry.totalUsd || 0) * 100),
              issued_at: entry.paidAt ? entry.paidAt.toISOString() : null,
              paid_at: entry.paidAt ? entry.paidAt.toISOString() : null,
              due_date: null,
              payment_intent_id: null,
              stripe_invoice_id: entry.stripeInvoiceId || null,
            }))}
            sourceNote={`Platform view sourced from paid invoice activity for ${selectedFeeCustomer?.companyName || 'the selected company'}.`}
            subtitlePrefix="Customer ID"
          />
        </>
    </PageLayout>
  );
}

export default PlatformBilling;
